import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./MosaicCarousel.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

export default function MosaicCarousel({
  slides = [],
  title,
  description,
  fontStyles,
  sectionStyles = {},
}) {
  const isMobile = useIsMobile();
  // 🧩 Normalize slides into consistent shape
  const normalized =
    Array.isArray(slides) && slides.length
      ? slides.map((item) => ({
          img: item.image || item.img || item.imageLink || "/placeholder.jpg",
          // explicit title + description for overlay
          title: item.title || item.name || item.label || "",
          description:
            item.description ||
            item.desc ||
            item.subtitle ||
            item.text ||
            "",
          link: item.link || item.href || "#",
        }))
      : [];

  // ⛓️ Chunk into groups of 6 items per slide
  const SLIDES = normalized.length
    ? normalized.reduce((acc, it, idx) => {
        const group = Math.floor(idx / 6);
        acc[group] = acc[group] || { items: [] };
        acc[group].items.push({
          key: String.fromCharCode(97 + (idx % 6)),
          img: it.img,
          title: it.title,
          description: it.description,
          link: it.link,
        });
        return acc;
      }, [])
    : [];

  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const autoRef = useRef(null);
  const touchStartX = useRef(0);
  const touchDelta = useRef(0);

  // 🎞️ Auto play every 3 seconds
  useEffect(() => {
    if (hover || SLIDES.length <= 1) return;
    autoRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 3000);
    return () => clearInterval(autoRef.current);
  }, [hover, SLIDES.length]);

  const go = (i) => setIndex((i + SLIDES.length) % SLIDES.length);
  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDelta.current = 0;
  };
  const onTouchMove = (e) => {
    touchDelta.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (Math.abs(touchDelta.current) > 50) {
      touchDelta.current < 0 ? next() : prev();
    }
  };

  if (!SLIDES.length) return null; // 🛑 No data → render nothing

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont = fontStyles?.sectionDescription || fontStyles?.subHeading || {};

  const sizeOverrides = sectionStyles?.fontSizes || {};

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.heading || sectionTitleFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color: sectionStyles?.headingColor || sectionTitleFont?.color || undefined,
  };

  const descStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || sectionDescFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color: sectionStyles?.subHeadingColor || sectionDescFont?.color || undefined,
  };

  const tileTitleStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 600,
    lineHeight: fontStyles?.content?.lineHeight || "1.4",
    color: sectionStyles?.contentColor || fontStyles?.content?.color || undefined,
  };

  return (
    <section
      className="mosaic-wrap"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="mosaic-inner">
        {(title || description) && (
          <header className="mosaic-header">
            {title && <h2 style={titleStyle}>{title}</h2>}
            {description && <p style={descStyle}>{description}</p>}
          </header>
        )}

        <div
          className="mosaic-track"
          style={{ transform: `translateX(-${index * 100}%)` }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {SLIDES.map((slide, sIdx) => (
            <div className="mosaic-slide" key={sIdx}>
              <div className="mosaic-grid">
                {slide.items.map((it) => (
                  <Link
                    key={it.key}
                    to={it.link || "#"}
                    className={`tile tile-${it.key}`}
                  >
                    <img src={it.img || "/placeholder.jpg"} alt={it.title} />
                    {(it.title || it.description) && (
                      <figcaption className="tile-label">
                        {it.title && <div style={tileTitleStyle}>{it.title}</div>}
                      
                      </figcaption>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {SLIDES.length > 1 && (
        <>
          <button className="mosaic-nav nav-left" onClick={prev}>
            ‹
          </button>
          <button className="mosaic-nav nav-right" onClick={next}>
            ›
          </button>

          <div className="mosaic-dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                className={`dot ${i === index ? "active" : ""}`}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
